import { useState } from 'react';
import { dashboardApi } from '../../services/dashboardApi';
import {
  BusinessStatusMetric,
  BusinessStatusPeriod,
  GaragesBusinessStatus,
  OrdersBusinessStatus,
  UsersBusinessStatus,
} from '../../types/audit';
import { formatGstTime } from '../audit/auditFormat';
import BlockErrorState from './BlockErrorState';
import { BUSINESS_STATUS_PERIODS, PERIOD_TEXT, readStoredPeriod, storePeriod } from './businessStatusFormat';
import BusinessStatusTile from './BusinessStatusTile';
import { RingMode } from './RingChart';
import { DashboardBlock, useDashboardBlock } from './useDashboardBlock';

interface BusinessStatusStripProps {
  token: string;
  /** The page's refresh counter: "refresh" re-reads the strip with the other blocks. */
  reloadKey: string;
}

interface TileSpec {
  id: string;
  label: string;
  ringMode: RingMode;
  sparkCaption: string;
  block: DashboardBlock<unknown>;
  pick: () => BusinessStatusMetric | undefined;
}

function SkeletonTile() {
  return (
    <div className="bs-tile bs-tile--skel" aria-hidden="true" data-testid="bs-skeleton">
      <div className="bs-skel bs-skel--label" />
      <div className="bs-tile__main">
        <div className="bs-skel bs-skel--ring" />
        <div className="bs-facts bs-facts--skel">
          <div className="bs-skel bs-skel--line" style={{ width: '85%' }} />
          <div className="bs-skel bs-skel--line" style={{ width: '60%' }} />
        </div>
      </div>
      <div className="bs-skel bs-skel--spark" />
    </div>
  );
}

/** Oldest `generatedAt` among the loaded blocks: the strip is as fresh as its stalest source. */
function updatedAt(blocks: DashboardBlock<{ generatedAt?: string } | null>[]): Date | null {
  let oldest: Date | null = null;
  for (const b of blocks) {
    if (b.status !== 'ok' || !b.data?.generatedAt) continue;
    const d = new Date(b.data.generatedAt);
    if (Number.isNaN(d.getTime())) continue;
    if (!oldest || d < oldest) oldest = d;
  }
  return oldest;
}

/**
 * Spec 070 — "Business status · last 30 days": four display-only tiles from three owning services,
 * each its own dashboard block (3 s timeout, 403/404 hides, other failures show Retry). The garages
 * block feeds two tiles, so its error shows on both and one Retry refetches it once.
 *
 * #1331 — Day · Week · Month in the header's corner sets the new/previous windows (24 h, 7 d, 30 d) of all
 * four tiles; the totals and sparklines don't change. The choice is remembered per browser.
 */
export default function BusinessStatusStrip({ token, reloadKey }: BusinessStatusStripProps) {
  const [period, setPeriod] = useState<BusinessStatusPeriod>(readStoredPeriod);
  const text = PERIOD_TEXT[period];
  const blockKey = `${reloadKey}:${period}`;
  const garages = useDashboardBlock<GaragesBusinessStatus>(
    token ? (s) => dashboardApi.garagesBusinessStatus(period, token, s) : null, blockKey);
  const users = useDashboardBlock<UsersBusinessStatus>(
    token ? (s) => dashboardApi.usersBusinessStatus(period, token, s) : null, blockKey);
  const orders = useDashboardBlock<OrdersBusinessStatus>(
    token ? (s) => dashboardApi.ordersBusinessStatus(period, token, s) : null, blockKey);

  const changePeriod = (p: BusinessStatusPeriod) => {
    if (p === period) return;
    storePeriod(p);
    setPeriod(p);
  };

  const tiles: TileSpec[] = [
    { id: 'garages', label: 'Garages live', ringMode: 'share', sparkCaption: 'New garages per month',
      block: garages, pick: () => garages.data?.garages },
    { id: 'services', label: 'Services offered', ringMode: 'share', sparkCaption: 'New services per month',
      block: garages, pick: () => garages.data?.services },
    { id: 'customers', label: 'Customers', ringMode: 'share', sparkCaption: 'Sign-ups per month',
      block: users, pick: () => users.data?.customers },
    { id: 'orders', label: `Orders, ${text.last}`, ringMode: 'period', sparkCaption: 'Orders per month',
      block: orders, pick: () => orders.data?.orders },
  ];

  const visible = tiles.filter((t) => t.block.status !== 'hidden' && t.block.status !== 'idle');
  if (visible.length === 0) return null;

  const busy = visible.some((t) => t.block.status === 'loading');
  const updated = updatedAt([garages, users, orders] as DashboardBlock<{ generatedAt?: string } | null>[]);

  return (
    <section className="dash-section bs-strip" aria-labelledby="bs-title" aria-busy={busy || undefined} data-testid="business-status">
      <div className="dash-section__head">
        <h3 id="bs-title" className="dash-section__title">
          Business status <span className="dash-section__sub">· {text.last}</span>
        </h3>
        <div className="bs-head-end">
          <span className="dash-section__aside bs-updated">
            Totals as of today · deltas vs the {text.span} before
            {updated && <> · <span data-testid="bs-updated">updated {formatGstTime(updated)}</span></>}
          </span>
          <div className="dash-toggle" role="group" aria-label="Business status period" data-testid="bs-period">
            {BUSINESS_STATUS_PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                className={`dash-toggle__btn${period === p ? ' dash-toggle__btn--active' : ''}`}
                aria-pressed={period === p}
                title={PERIOD_TEXT[p].last}
                onClick={() => changePeriod(p)}
              >
                {PERIOD_TEXT[p].option}
              </button>
            ))}
          </div>
        </div>
      </div>
      {busy && <p className="sr-only" role="status">Loading business status</p>}
      <div className="bs-grid">
        {visible.map((t) => {
          const { block } = t;
          if (block.status === 'loading') return <SkeletonTile key={t.id} />;
          if (block.status === 'error') {
            return (
              <div key={t.id} className="bs-tile-error" data-tile={t.id}>
                <BlockErrorState title={t.label} message={block.error} onRetry={block.retry} />
              </div>
            );
          }
          const metric = t.pick();
          if (!metric) return null;
          // Until a re-read lands the tile keeps the numbers it has, so it labels them with their own period.
          const shown = (block.data as { period?: BusinessStatusPeriod } | null)?.period ?? period;
          return (
            <BusinessStatusTile key={t.id} id={t.id} label={t.id === 'orders' ? `Orders, ${PERIOD_TEXT[shown].last}` : t.label}
              metric={metric} ringMode={t.ringMode} sparkCaption={t.sparkCaption} period={shown} />
          );
        })}
      </div>
    </section>
  );
}
